/**
 * Persist the active game tab across page navigation.
 *
 * Any game code is accepted now - the pair 3min/5min used to be validated
 * here, which silently pinned every screen to 3min once other games
 * existed. Whether a stored code is still valid is the caller's business,
 * because only it knows which games are running.
 */
const LIVE_SESSION_TAB_KEY = "yantra_active_game_tab";

export const getStoredActiveGameTab = () => {
  try {
    return localStorage.getItem(LIVE_SESSION_TAB_KEY) || "";
  } catch {
    return "";
  }
};

export const setStoredActiveGameTab = (tab) => {
  try {
    if (tab) localStorage.setItem(LIVE_SESSION_TAB_KEY, String(tab));
  } catch {}
};

/** Persist All Draw page filters (date, game type) when navigating to session-winning-detail */
const ALL_DRAW_DATE_KEY = "yantra_all_draw_date";

const getTodayDateString = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

export const getStoredAllDrawDate = () => {
  try {
    const stored = localStorage.getItem(ALL_DRAW_DATE_KEY);
    const today = getTodayDateString();
    if (!stored || stored > today) return today;
    return stored;
  } catch {
    return getTodayDateString();
  }
};

export const setStoredAllDrawDate = (date) => {
  try {
    if (date && typeof date === "string") {
      localStorage.setItem(ALL_DRAW_DATE_KEY, date);
    }
  } catch {}
};

/** Cache All Draw data when navigating to session-winning-detail (avoids reload on back) */
const ALL_DRAW_CACHE_KEY = "yantra_all_draw_cache";

export const setAllDrawCache = (date, gameType, draws, scrollTop) => {
  try {
    const payload = JSON.stringify({
      date,
      gameType,
      draws: draws || [],
      scrollTop: Number(scrollTop) || 0,
    });
    sessionStorage.setItem(ALL_DRAW_CACHE_KEY, payload);
  } catch (_) {}
};

export const getAllDrawCache = () => {
  try {
    const raw = sessionStorage.getItem(ALL_DRAW_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.date === "string" && typeof parsed.gameType === "string") {
      return parsed;
    }
    return null;
  } catch (_) {
    return null;
  }
};

export const clearAllDrawCache = () => {
  try {
    sessionStorage.removeItem(ALL_DRAW_CACHE_KEY);
  } catch (_) {}
};

export const AVATAR_COLORS = [
  "#2563EB", // blue
  "#16A34A", // green
  "#DC2626", // red
  "#7C3AED", // purple
  "#EA580C", // orange
  "#0891B2", // cyan
  "#9333EA", // violet
  "#CA8A04", // yellow
  "#0D9488", // teal
  "#4F46E5", // indigo
  "#DB2777", // pink
  "#65A30D", // lime
];

export const getInitials = (name = "") => {
  if (!name) return "";

  const words = name.trim().split(" ");
  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }
  return (
    words[0][0].toUpperCase() +
    words[words.length - 1][0].toUpperCase()
  );
};

export const getAvatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

export const selectCustomStyles = {
  control: (provided) => ({
    ...provided,
    minWidth: "200px",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

//live yantra image url
export const LIVE_YANTRA_IMAGE_URL = `${process.env.REACT_APP_NODE_URL}/api/yantra`;
// export const LIVE_YANTRA_IMAGE_URL = `${process.env.REACT_APP_NODE_URL}/yantra_img`;

/** Rupees credited per one “silver coin” shown on win modal / print (₹600 win → 6 coins). */
export const SILVER_COIN_RUPEE_VALUE = 100;